import { useEffect } from "react";

const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Cipher & Row",
  "legalName": "Cipher & Row, Inc.",
  "url": "https://cipherandrow.com",
  "logo": "https://cipherandrow.com/favicon.png",
  "description": "Canada-first carrier and broker verification, monitoring, and compliance for the freight industry.",
  "foundingDate": "2025",
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "support@cipherandrow.com",
    "contactType": "customer support"
  },
  "founder": [
    {
      "@type": "Person",
      "name": "Jack Nowlan",
      "jobTitle": "Co-Founder"
    },
    {
      "@type": "Person",
      "name": "Louis-Charles Gagnon",
      "jobTitle": "Co-Founder"
    }
  ],
  "sameAs": [
    "https://www.linkedin.com/company/cipherandrow"
  ]
};

const RETURN_POLICY = {
  "@type": "MerchantReturnPolicy",
  "applicableCountry": ["US", "CA"],
  "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
  "merchantReturnDays": 30,
  "returnMethod": "https://schema.org/ReturnByMail",
  "returnFees": "https://schema.org/FreeReturn"
};

const SHIPPING_DETAILS = {
  "@type": "OfferShippingDetails",
  "shippingRate": {
    "@type": "MonetaryAmount",
    "value": "0",
    "currency": "USD"
  },
  "shippingDestination": [
    { "@type": "DefinedRegion", "addressCountry": "US" },
    { "@type": "DefinedRegion", "addressCountry": "CA" }
  ],
  "deliveryTime": {
    "@type": "ShippingDeliveryTime",
    "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" },
    "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" }
  }
};

const PRODUCT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Cipher & Row Verification Platform",
  "description": "Carrier and broker verification with continuous monitoring. Know the moment authority, bond, insurance, or safety status changes. US and Canada coverage.",
  "url": "https://cipherandrow.com",
  "image": "https://cipherandrow.com/og/home.jpg",
  "brand": {
    "@type": "Brand",
    "name": "Cipher & Row"
  },
  "offers": [
    {
      "@type": "Offer",
      "name": "Carrier Plan",
      "price": "99",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "99",
        "priceCurrency": "USD",
        "unitCode": "MON"
      },
      "description": "Broker lookup and monitoring alerts. Get notified when a broker's authority, bond, or insurance changes.",
      "url": "https://cipherandrow.com/pricing",
      "hasMerchantReturnPolicy": RETURN_POLICY,
      "shippingDetails": SHIPPING_DETAILS
    },
    {
      "@type": "Offer",
      "name": "Dispatcher Plan",
      "price": "99",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "99",
        "priceCurrency": "USD",
        "unitCode": "MON"
      },
      "description": "Carrier and broker lookup with monitoring alerts. Know before you dispatch.",
      "url": "https://cipherandrow.com/pricing",
      "hasMerchantReturnPolicy": RETURN_POLICY,
      "shippingDetails": SHIPPING_DETAILS
    },
    {
      "@type": "Offer",
      "name": "Broker Plan",
      "price": "250",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "250",
        "priceCurrency": "USD",
        "unitCode": "MON"
      },
      "description": "Carrier lookup and monitoring alerts. Get notified when a carrier's authority, CVOR, CTQ, NSC, or insurance changes.",
      "url": "https://cipherandrow.com/pricing",
      "hasMerchantReturnPolicy": RETURN_POLICY,
      "shippingDetails": SHIPPING_DETAILS
    }
  ]
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Cipher & Row?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cipher & Row verifies carriers and brokers against FMCSA, CVOR, CTQ, and NSC registries, then monitors them for compliance changes. Canada-first. Alerts when authority, bond, or insurance changes."
      }
    },
    {
      "@type": "Question",
      "name": "Do you verify Canadian carriers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Ontario CVOR, Quebec CTQ including courtage permits, federal NSC, CRA Business Number via Corporations Canada, and FMCSA for cross-border operators. All in one search."
      }
    },
    {
      "@type": "Question",
      "name": "What does verification check?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Authority status, insurance on file, bond status, safety rating, fleet size, out-of-service history, fraud signals, and trust tier. US runs through FMCSA. Canada runs through CVOR, CTQ, and NSC."
      }
    },
    {
      "@type": "Question",
      "name": "How much does it cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Carriers are free always with unlimited broker lookups. Dispatchers are $49/mo Solo or $99/mo Pro. Brokers are $149/mo Compliance, $399/mo Pro, $999/mo Scale, or Enterprise from $2,500/mo. Annual billing is 20% off."
      }
    },
    {
      "@type": "Question",
      "name": "Can I integrate via API?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Every plan includes a REST API and webhooks. An MCP server with verification and monitoring tools is available for AI agent integration at cipherandrow.com/integrate."
      }
    },
    {
      "@type": "Question",
      "name": "Do you support brokers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Brokers get carrier lookup and monitoring starting at $149/mo (Compliance). Carriers and dispatchers can look up broker bond status, authority, and insurance for free."
      }
    },
    {
      "@type": "Question",
      "name": "Is there a free tier?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "One-time lookups are free at cipherandrow.com/verify, no signup needed. Continuous monitoring and alerts start at $29/mo for carriers (Verified+) or $49/mo for dispatchers (Solo)."
      }
    }
  ]
};

function injectSchema(id: string, schema: object) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
}

function removeSchema(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function MarketingHead() {
  useEffect(() => {
    injectSchema("ld-marketing-org", ORGANIZATION_SCHEMA);
    injectSchema("ld-marketing-product", PRODUCT_SCHEMA);
    injectSchema("ld-marketing-faq", FAQ_SCHEMA);

    return () => {
      removeSchema("ld-marketing-org");
      removeSchema("ld-marketing-product");
      removeSchema("ld-marketing-faq");
    };
  }, []);

  return null;
}
